import { useState, useEffect, useRef } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import viaCepApi from 'services/viaCepApi'
import MaskedInputService from 'services/maskedInput'
import ChangePictureModal from 'pages/Modals/ChangePictureModal'
import AlertDialog from 'components/AlertDialog'
import editBusinessValidator from 'validators/editBusinessValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'
import { Formik } from 'formik'

import {
  Container,
  Form,
  Button,
  BigImage,
  ImageContainer,
  AddressContainer,
  SubTitle,
  FormField,
  WarningText,
  ErrorContainer,
  LoadingBackdrop,
  Input,
  MaskedInput,
  Row
} from '../styles'

export default function EditCompany () {
  const [loading, setLoading] = useState (true)
  const [company, setCompany] = useState (null)
  const [errorMessage, setErrorMessage] = useState (null)
  const [loadingAddress, setLoadingAddress] = useState (false)
  const [showPictureDialog, setShowPictureDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const token = AuthService.getToken ()
  const user = AuthService.getUser ()
  const address = company?.addresses[0]

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    try {
      const { data: company } = await cutileiApi.get (`/companies/${user.id}`)

      setCompany (company)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const searchAddress = async (event, setFieldValue) => {
    const postal_code = event.target.rawValue

    setFieldValue ('address.postal_code', postal_code)
    
    if (postal_code.length === 8) {
      setLoadingAddress (true)
      try {
        const response = await viaCepApi.get (`/ws/${postal_code}/json`)
        const { data } = response

        setFieldValue ('address.route', data.logradouro)
        setFieldValue ('address.district', data.bairro)
        setFieldValue ('address.city', data.localidade)
        setFieldValue ('address.state', data.uf)
      } catch (error) {
        logApiError (error)
      } finally {
        setLoadingAddress (false)
      }
    }
  }

  const handleChangePicture = newPictureUrl => {
    AuthService.updateUser ({company: {...user, logo: newPictureUrl}})
    setCompany ({...company, logo: newPictureUrl})
    togglePictureDialog ()
  }

  const togglePictureDialog = () => setShowPictureDialog (!showPictureDialog)
  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  return (
    <Container>
      {loading || !company ? <Loading/> : (
        <Formik
          validationSchema={editBusinessValidator}
          validateOnChange={false}
          validateOnBlur={true}
          initialValues={{
            'name': company.name,
            'corporate_name': company.corporate_name,
            'email': company.email,
            'cnpj': company.cnpj,
            'cellphone': company.cellphone,
            'phone': company.phone,
            'owner': company.owner,
            'owner_email': company.owner_email,
            'owner_phone': company.owner_phone,
            'address': {
              id: address.id,
              route: address.route,
              number: address.number,
              complement: address.complement,
              district: address.district,
              city: address.city,
              state: address.state,
              postal_code: address.postal_code,
              latitude: address.latitude,
              longitude: address.longitude
            },
          }}
          onSubmit={async (values, { setSubmitting }) => {
            const {
              name,
              corporate_name,
              owner,
              owner_email,
              owner_phone,
              email,
              cnpj,
              phone,
              cellphone,
              address
            } = values

            setErrorMessage (null)
            setSubmitting (true)

            try {
              await cutileiApi.put (`/companies/${company.id}`, {
                name,
                corporate_name,
                owner,
                owner_email,
                owner_phone,
                email,
                cnpj,
                phone,
                cellphone,
                address
              }, requestConfig)

              setSubmitting (false)
              toggleOkDialog ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {function EditCompanyForm ({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleChange,
            handleBlur,
            handleSubmit
          }) {
            return (
              <Form onSubmit={handleSubmit}>
                <SubTitle>Dados da empresa</SubTitle>
                <ImageContainer onClick={togglePictureDialog}>
                  <BigImage src={company.logo}/>
                </ImageContainer>
                <Row>
                  <FormField stretch={2}>
                    <Input
                      placeholder='Nome fantasia'
                      value={values.name}
                      onChange={handleChange ('name')}
                      onBlur={handleBlur ('name')}
                    />
                    {touched.name && errors.name && (
                      <WarningText>{errors.name}</WarningText>
                    )}
                  </FormField>
                  <FormField stretch={2}>
                    <Input
                      placeholder='Razão social'
                      value={values.corporate_name}
                      onChange={handleChange ('corporate_name')}
                      onBlur={handleBlur ('corporate_name')}
                    />
                    {touched.corporate_name && errors.corporate_name && (
                      <WarningText>{errors.corporate_name}</WarningText>
                    )}
                  </FormField>
                  <FormField stretch={2}>
                    <MaskedInput
                      name='cnpj'
                      placeholder='CNPJ'
                      options={{
                        blocks: [2,3,3,4,2],
                        delimiters: ['.', '.','/', '-'],
                        numericOnly: true
                      }}
                      value={values.cnpj}
                      onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                      onBlur={handleBlur ('cnpj')}
                    />
                    {touched.cnpj && errors.cnpj && (
                      <WarningText>{errors.cnpj}</WarningText>
                    )}
                  </FormField>
                </Row>
                <Row>
                  <FormField stretch={2}>
                    <Input
                      type='email'
                      placeholder='E-mail'
                      value={values.email}
                      onChange={handleChange ('email')}
                      onBlur={handleBlur ('email')}
                    />
                    {touched.email && errors.email && (
                      <WarningText>{errors.email}</WarningText>
                    )}
                  </FormField>
                  <FormField>
                    <MaskedInput
                      name='cellphone'
                      placeholder='Celular'
                      options={{
                        blocks: [0, 2, 5, 4],
                        delimiters: ['(', ') ', '-'],
                        numericOnly: true,
                      }}
                      value={values.cellphone}
                      onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                      onBlur={handleBlur ('cellphone')}
                    />
                    {touched.cellphone && errors.cellphone && (
                      <WarningText>{errors.cellphone}</WarningText>
                    )}
                  </FormField>
                  <FormField>
                    <MaskedInput
                      name='phone'
                      placeholder='Telefone fixo (opcional)'
                      options={{
                        blocks: [0, 2, 4, 4],
                        delimiters: ['(', ') ', '-'],
                        numericOnly: true,
                      }}
                      value={values.phone}
                      onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                      onBlur={handleBlur ('phone')}
                    />
                    {touched.phone && errors.phone && (
                      <WarningText>{errors.phone}</WarningText>
                    )}
                  </FormField>
                </Row>
                <SubTitle>Dados do responsável</SubTitle>
                <Row>
                  <FormField>
                    <Input
                      placeholder='Nome completo do responsável'
                      value={values.owner}
                      onChange={handleChange ('owner')}
                      onBlur={handleBlur ('owner')}
                    />
                    {touched.owner && errors.owner && (
                      <WarningText>{errors.owner}</WarningText>
                    )}
                  </FormField>
                  <FormField>
                    <Input
                      type='email'
                      placeholder='E-mail do responsável'
                      value={values.owner_email}
                      onChange={handleChange ('owner_email')}
                      onBlur={handleBlur ('owner_email')}
                    />
                    {touched.owner_email && errors.owner_email && (
                      <WarningText>{errors.owner_email}</WarningText>
                    )}
                  </FormField>
                  <FormField>
                    <MaskedInput
                      name='owner_phone'
                      placeholder='Celular do responsável'
                      options={{
                        blocks: [0, 2, 5, 4],
                        delimiters: ['(', ') ', '-'],
                        numericOnly: true
                      }}
                      value={values.owner_phone}
                      onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                      onBlur={handleBlur ('owner_phone')}
                    />
                    {touched.owner_phone && errors.owner_phone && (
                      <WarningText>{errors.owner_phone}</WarningText>
                    )}
                  </FormField>
                </Row>
                <SubTitle>Endereço</SubTitle>
                <AddressContainer>
                  {loadingAddress && (
                    <LoadingBackdrop>
                      <Loading/>
                    </LoadingBackdrop>
                  )}
                  <Row>
                    <FormField>
                      <MaskedInput
                        name='address.postal_code'
                        placeholder='CEP'
                        options={{
                          blocks: [5,3],
                          delimiters: ['-'],
                          numericOnly: true
                        }}
                        value={values.address.postal_code}
                        onChange={event => searchAddress (event, setFieldValue)}
                        onBlur={handleBlur ('address.postal_code')}
                      />
                      {touched.address?.postal_code && errors.address?.postal_code && (
                        <WarningText>{errors.address.postal_code}</WarningText>
                      )}
                    </FormField>
                    <FormField stretch={2}>
                      <Input placeholder='Rua/avenida' value={values.address.route} disabled/>
                    </FormField>
                    <FormField>
                      <Input
                        placeholder='Número'
                        value={values.address.number}
                        onChange={handleChange ('address.number')}
                        onBlur={handleBlur ('address.number')}
                      />
                      {touched.address?.number && errors.address?.number && (
                        <WarningText>{errors.address.number}</WarningText>
                      )}
                    </FormField>
                    <FormField>
                      <Input
                        placeholder='Complemento'
                        value={values.address.complement}
                        onChange={handleChange ('address.complement')}
                        onBlur={handleBlur ('address.complement')}
                      />
                    </FormField>
                  </Row>
                  <Row>
                    <FormField stretch={2}>
                      <Input placeholder='Bairro' value={values.address.district} disabled/>
                    </FormField>
                    <FormField>
                      <Input placeholder='Cidade' value={values.address.city} disabled/>
                    </FormField>
                    <FormField>
                      <Input placeholder='Estado' value={values.address.state} disabled/>
                    </FormField>
                  </Row>
                </AddressContainer>
                {errorMessage && (
                  <ErrorContainer>
                    <p>{errorMessage}</p>
                  </ErrorContainer>
                )}
                <AlertDialog
                  visible={showOkDialog}
                  title='Sucesso'
                  message='Alterações salvas!'
                  confirmText='Ok'
                  onConfirm={toggleOkDialog}
                  onClose={toggleOkDialog}
                />
                <Button type='submit' disabled={isSubmitting}>
                  {isSubmitting ? <ButtonLoading/> : 'Salvar alterações'}
                </Button>
              </Form>
            )
          }}
        </Formik>
      )}
      <ChangePictureModal
        title='Alterar foto de perfil'
        visible={showPictureDialog}
        onConfirm={handleChangePicture}
        onClose={togglePictureDialog}
      />
    </Container>
  )
}
